import { ApolloError } from '@apollo/client';
import { Stack } from '@mui/material';

import {
  LoadedContent,
  NO_DATA_PLACEHOLDER,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Typography,
} from '@occc-app/design-system';
import { DateTimeFormat, formatDateTime } from '@occc-app/lib/date';
import { RetailerNetworkLocationPosConnectionStatusFragment } from '@occc/graphql';

import { StatusDot } from './status-dot.component';
import {
  ConnectionStatus,
  HARDWARE_VENDOR_LABELS,
  getConnectionStatus,
  getScanDataStatus,
} from './connection-status-card.utils';

type ConnectionStatusCardProps = {
  terminals?: RetailerNetworkLocationPosConnectionStatusFragment[];
  loading: boolean;
  error?: ApolloError;
};

const formatTimestamp = (timestamp?: string | null) =>
  timestamp
    ? formatDateTime(new Date(timestamp), DateTimeFormat.DateTimeYearFirstSlashed24h)
    : null;

type ConnectionStatusCellProps = {
  status: ConnectionStatus;
  timestamp?: string | null;
};

const ConnectionStatusCell = ({ status, timestamp }: ConnectionStatusCellProps) => {
  const formattedTimestamp = formatTimestamp(timestamp);

  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <StatusDot variant={status.variant} />
      <Typography variant='body2'>{formattedTimestamp ?? status.label}</Typography>
    </Stack>
  );
};

const renderTerminals = (terminals: RetailerNetworkLocationPosConnectionStatusFragment[]) => (
  <TableContainer>
    <TableHeader title='Connection Status' />
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Terminal</TableCell>
          <TableCell>POS Type</TableCell>
          <TableCell>Loyalty</TableCell>
          <TableCell>Mobile</TableCell>
          <TableCell>Scan Data (Last Submission)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {terminals.map((terminal, index) => (
          <TableRow key={`${terminal.identifier ?? 'terminal'}-${index}`}>
            <TableCell>{terminal.identifier || NO_DATA_PLACEHOLDER}</TableCell>
            <TableCell>
              {(terminal.hardwareVendor && HARDWARE_VENDOR_LABELS[terminal.hardwareVendor]) ??
                NO_DATA_PLACEHOLDER}
            </TableCell>
            <TableCell>
              <ConnectionStatusCell
                status={getConnectionStatus(terminal.loyaltyEnabled, terminal.loyaltyConnectedAt)}
                // Disabled means the timestamp isn't meaningful even if one happens to be
                // present — withhold it so the cell always shows "Not Connected" instead.
                timestamp={terminal.loyaltyEnabled ? terminal.loyaltyConnectedAt : null}
              />
            </TableCell>
            <TableCell>
              <ConnectionStatusCell
                status={getConnectionStatus(terminal.mobileEnabled, terminal.mobileConnectedAt)}
                timestamp={terminal.mobileEnabled ? terminal.mobileConnectedAt : null}
              />
            </TableCell>
            <TableCell>
              <ConnectionStatusCell
                status={getScanDataStatus(terminal.scanDataSubmittedAt)}
                timestamp={terminal.scanDataSubmittedAt}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const ConnectionStatusCard = ({ terminals, loading, error }: ConnectionStatusCardProps) => (
  <LoadedContent<RetailerNetworkLocationPosConnectionStatusFragment>
    loading={loading}
    error={error}
    items={terminals}
    renderData={renderTerminals}
    renderEmpty={() => <></>}
    redirectIfEmpty={false}
  />
);
